import MainTable from '@Components/MainTable';
import NoResults from '@Components/NoResults/NoResults';
import { WrappedTableCell } from '@Components/UI/Tables/Tables.styled';
import { egp } from '@ContentManagement/EnrollmentInvoices';
import { TableHeader } from '@Features/generics/generics.type';
import { Box, TableCell, TableRow, ToggleButton, ToggleButtonGroup } from '@mui/material';
import { useSnackbar } from '@Providers/useSnackbar';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import DistributionDonuts from '../Components/DistributionDonuts';
import HubDateRange, { HubRange } from '../Components/HubDateRange';
import { RevenueSummaryCards } from '../Components/SummaryCards';
import WrappedHeadsTable from '../Components/WrappedHeadsTable';
import { analyticsSeasonStart } from '../Constants/methods';
import { EducationStatisticsRequest, OverviewRequest } from '../Services/Analytics.req';
import { EducationRow, OverviewData } from '../Types/Analytics.types';

type Dimension = 'year' | 'section' | 'subject';

const DIMENSION_LABELS: Record<Dimension, string> = {
	// Platform vocabulary: education years are the grades, education sections
	// are the tracks, and categories are surfaced to admins as "Subjects".
	year: 'Year',
	section: 'Section',
	subject: 'Subject',
};

const TABLE_HEADS = (dimension: Dimension): TableHeader[] => [
	{ displayName: DIMENSION_LABELS[dimension], fieldName: 'name', canSort: false, width: '18%' },
	{ displayName: 'Enrollments', fieldName: 'enrollments', canSort: false },
	{ displayName: 'Wallet Revenue', fieldName: 'collected', canSort: false },
	{ displayName: 'Scratchcards Revenue', fieldName: 'scratchcard_value', canSort: false },
	{ displayName: 'Total Revenue', fieldName: 'total_revenue', canSort: false },
	{ displayName: '% of Enrollments', fieldName: 'enrollments_share', canSort: false },
	{ displayName: '% of Revenue', fieldName: 'revenue_share', canSort: false },
];

/** Grade / track / subject splits of enrollments and money. */
const EducationTab: React.FC<{ instructorId?: number }> = ({ instructorId }) => {
	const { displaySnackbar } = useSnackbar();
	const [dimension, setDimension] = useState<Dimension>('year');
	const [rows, setRows] = useState<EducationRow[]>([]);
	const [totals, setTotals] = useState<{ enrollments: number; collected: number; total_revenue: number } | null>(null);
	const [overview, setOverview] = useState<OverviewData | null>(null);
	const seasonRange = useMemo(() => ({ from: analyticsSeasonStart() }), []);
	const [range, setRange] = useState<HubRange>(seasonRange);

	// KPI cards on top, sharing the tab's date range and scope.
	const loadOverviewCards = useCallback(
		async (nextRange: { from?: Date; to?: Date } = {}) => {
			try {
				const response = await OverviewRequest({
					from: nextRange.from,
					to: nextRange.to,
					instructor_id: instructorId,
				});
				setOverview(response.data.data || null);
			} catch {
				/* the table below reports errors */
			}
		},
		[instructorId]
	);

	const load = useCallback(
		async (
			nextDimension: Dimension,
			nextRange: { from?: Date; to?: Date } = {}
		) => {
			try {
				const response = await EducationStatisticsRequest({
					dimension: nextDimension,
					from: nextRange.from,
					to: nextRange.to,
					instructor_id: instructorId,
				});
				const data = response.data.data || {};
				setRows(data.rows || []);
				setTotals(data.totals || null);
			} catch (error: any) {
				displaySnackbar(
					'error',
					error.response?.data?.message || 'Unable to load education statistics'
				);
			}
		},
		[displaySnackbar, instructorId]
	);

	// The tab's HubDateRange drives the cards, donuts and table together.
	useEffect(() => {
		load(dimension, range);
		loadOverviewCards(range);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [dimension, instructorId, range]);

	const share = (value: number, total?: number | null) =>
		total ? `${((value / total) * 100).toFixed(1)}%` : '-';

	const heads = TABLE_HEADS(dimension);
	const attribution =
		dimension === 'subject'
			? 'By product subject.'
			: "By the purchasing student's CURRENT education profile — moving a student to another year/section re-buckets their whole history.";

	return (
		<TabPage>
			<Box display="flex" justifyContent="space-between" alignItems="center" mb="1.5rem">
				<ToggleButtonGroup
					value={dimension}
					exclusive
					size="small"
					onChange={(_, next) => next && setDimension(next)}
				>
					{(Object.keys(DIMENSION_LABELS) as Dimension[]).map((key) => (
						<ToggleButton key={key} value={key}>
							{DIMENSION_LABELS[key]}
						</ToggleButton>
					))}
				</ToggleButtonGroup>
				<HubDateRange initialRange={seasonRange} onApply={(nextRange) => setRange(nextRange)} />
			</Box>

			{overview && (
				<RevenueSummaryCards
					showRefundCards={false}
					summary={{
						methods: overview.methods,
						grand_total: overview.revenue.grand_total,
						enrollments: overview.enrollment_summary.purchase_methods_total,
						discounts: overview.revenue.discounts,
						refunded: overview.revenue.refunded,
						refund_count: 0,
						net: overview.revenue.total_revenue,
					}}
				/>
			)}

			<DistributionDonuts
				enrollments={rows.map((row) => ({
					id: row.bucket_id ?? 0,
					name: row.name,
					value: row.enrollments,
				}))}
				revenue={rows.map((row) => ({
					id: row.bucket_id ?? 0,
					name: row.name,
					value: row.total_revenue,
				}))}
				enrollmentsTitle={`Enrollments by ${DIMENSION_LABELS[dimension].toLowerCase()}`}
				revenueTitle={`Total Revenue by ${DIMENSION_LABELS[dimension].toLowerCase()}`}
			/>

			<WrappedHeadsTable>
			<MainTable
				tableFilters={[]}
				RowsperPage={rows.length || 10}
				totalPages={1}
				total={rows.length}
				pageNum={1}
				tableHeads={heads}
				dateFilter={false}
				tableMinWidth="55rem"
				renderItems={() => {
					if (!rows.length) {
						return (
							<TableRow>
								<TableCell colSpan={heads.length}>
									<NoResults />
								</TableCell>
							</TableRow>
						);
					}
					return rows.map((row) => (
						<TableRow key={`${row.bucket_id ?? 'none'}-${row.name}`}>
							<WrappedTableCell align="center">{row.name}</WrappedTableCell>
							<TableCell align="center">{row.enrollments}</TableCell>
							<TableCell align="center">{egp(row.collected)}</TableCell>
							<TableCell align="center">{egp(row.scratchcard_value)}</TableCell>
							<TableCell align="center">{egp(row.total_revenue)}</TableCell>
							<TableCell align="center">{share(row.enrollments, totals?.enrollments)}</TableCell>
							<TableCell align="center">{share(row.total_revenue, totals?.total_revenue)}</TableCell>
						</TableRow>
					));
				}}
			/>
			</WrappedHeadsTable>
			<Footnote>{attribution}</Footnote>
		</TabPage>
	);
};

const TabPage = styled.div`
	width: 100%;
	max-width: 100%;
	min-width: 0;
	padding-bottom: 3rem;
`;

const Footnote = styled.div`
	font-size: 0.8rem;
	color: rgba(0, 0, 0, 0.55);
	margin-top: 0.5rem;
`;

export default EducationTab;
